import React from 'react';
import { formatDurationValue } from '../utils/valueFormatting';
import { AnalyticsMetric } from '../const';

const CONVERSATION_METRIC_TYPES = [
  AnalyticsMetric.SUCCESS_RATE,
  AnalyticsMetric.ERROR_RATE,
  AnalyticsMetric.USER_ABANDONED,
] as const;

export interface ChartTooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number | string;
    payload?: any;
    color?: string;
  }>;
  label?: string | number;
  title?: string;
  unit?: string;
  showPercentage?: boolean;
  showOriginalValue?: boolean;
  showColorIndicator?: boolean;
  numberOfConversations?: number;
  chartType?: string;
}

export const ChartTooltip: React.FC<any> = props => {
  const {
    active,
    payload,
    unit,
    showPercentage = false,
    showOriginalValue = false,
    showColorIndicator = false,
    numberOfConversations,
    chartType,
  } = props;

  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const data = payload[0].payload;
  const value = payload[0].value;

  const displayLabel = data.name;

  const formatValue = (
    val: number | string,
    unitType: string | object | undefined
  ) => {
    const numValue = typeof val === 'string' ? parseFloat(val) : val;

    if (
      unitType &&
      typeof unitType === 'object' &&
      'minute' in unitType &&
      'seconds' in unitType
    ) {
      const duration = formatDurationValue(numValue);
      if (duration.minutes > 0) {
        return `${duration.minutes}m ${duration.seconds}s`;
      }
      return `${duration.seconds}s`;
    }

    return numValue.toString();
  };

  const formattedValue = formatValue(value, unit);

  return (
    <div
      className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg"
      style={{
        backgroundColor: '#ffffff',
        opacity: 1,
        zIndex: 9999,
        position: 'relative',
        pointerEvents: 'none',
      }}
    >
      {displayLabel && (
        <div className="flex items-center gap-2 mb-1">
          {!CONVERSATION_METRIC_TYPES.includes(chartType || '') && (
            <>
              {showColorIndicator && data.color && (
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: data.color }}
                />
              )}
              <p className="text-sm font-medium text-gray-900">
                {String(displayLabel)}
              </p>
            </>
          )}
        </div>
      )}
      <div className="text-sm text-gray-600">
        {!CONVERSATION_METRIC_TYPES.includes(chartType || '') && (
          <>
            {showPercentage && data.percentage !== undefined && (
              <span>Value: {data.percentage}%</span>
            )}
            {showPercentage &&
              data.percentage !== undefined &&
              showOriginalValue && <span> - </span>}
            {showOriginalValue && data.originalValue !== undefined && (
              <span className="font-bold">{data.originalValue}</span>
            )}
            {!showPercentage && !showOriginalValue && (
              <span>
                Value: <span className="font-bold">{formattedValue}</span>
                {unit && typeof unit === 'string' && ` ${unit}`}
              </span>
            )}
          </>
        )}
        {numberOfConversations !== undefined && (
          <div className="mt-1 text-sm text-gray-600">
            {CONVERSATION_METRIC_TYPES.includes(chartType || '') &&
            data.selectedConversations !== undefined ? (
              <>
                {data.name}:{' '}
                <span className="font-bold">{data.selectedConversations}</span>
              </>
            ) : (
              <>
                Total Conversations:{' '}
                <span className="font-bold">{numberOfConversations}</span>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChartTooltip;
